import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconCar,
  IconDots,
  IconPencil,
  IconPlus,
  IconTrash,
  IconUsersGroup,
} from "@tabler/icons-react";
import { DataTable, DataTableColumn } from "@/components/DataTable";
import { FormModal } from "@/components/FormModal";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Can } from "@/components/Can";
import { api } from "@/lib/api";
import { formatAPIError, qk } from "@/lib/apiHelpers";
import type {
  Carrier,
  CarriersListResponse,
  Vehicle,
  VehicleCreateRequest,
  VehiclesListResponse,
  VehicleUpdateRequest,
} from "@/types/api";
import { VehicleForm, VehicleFormApi, VehicleFormValues } from "./VehicleForm";
import { VehicleDrivers } from "./VehicleDrivers";

const KIND_OPTIONS = [
  { value: "", label: "Все" },
  { value: "single", label: "Одиночная" },
  { value: "autopoezd", label: "Автопоезд" },
];

export default function VehiclesPage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<string | null>(null);
  const [carrierFilter, setCarrierFilter] = useState<string | null>(null);
  const [debounced] = useDebouncedValue(search, 300);

  const filters = {
    page,
    page_size: 50,
    q: debounced,
    kind: kindFilter ?? undefined,
    carrier_id: carrierFilter ?? undefined,
  };

  const { data, isLoading } = useQuery({
    queryKey: qk.vehicles(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(filters.page));
      params.set("page_size", String(filters.page_size));
      if (filters.q) params.set("q", filters.q);
      if (filters.kind) params.set("kind", filters.kind);
      if (filters.carrier_id) params.set("carrier_id", filters.carrier_id);
      return (await api.get<VehiclesListResponse>(`/api/v1/vehicles?${params.toString()}`)).data;
    },
  });

  const { data: carriersData } = useQuery({
    queryKey: qk.carriers({ page: 1, page_size: 200 }),
    queryFn: async () =>
      (await api.get<CarriersListResponse>("/api/v1/carriers?page=1&page_size=200")).data,
  });
  const carrierOptions = [
    { value: "", label: "Все перевозчики" },
    ...(carriersData?.items ?? []).map((c: Carrier) => ({ value: c.id, label: c.name })),
  ];

  const [editing, setEditing] = useState<Vehicle | null | undefined>(undefined);

  // Поддержка ?new=1&carrier_id=X из карточки перевозчика (#93). При mount
  // открываем форму создания с предзаполненным carrier_id. Очищаем query
  // чтобы не было «залипания» при возврате на страницу.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const carrierID = searchParams.get("carrier_id") ?? "";
      setEditing({ carrier_id: carrierID } as Vehicle);
      searchParams.delete("new");
      searchParams.delete("carrier_id");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // formApi через useRef — см. длинный коммент в Drivers/index.tsx.
  const formApiRef = useRef<VehicleFormApi | null>(null);
  const setFormApi = useCallback((api: VehicleFormApi) => {
    formApiRef.current = api;
  }, []);
  const [confirmDelete, setConfirmDelete] = useState<Vehicle | null>(null);
  const [driversVehicle, setDriversVehicle] = useState<Vehicle | null>(null);

  const buildBody = (vals: VehicleFormValues): VehicleCreateRequest => ({
    kind: vals.kind,
    brand: vals.brand,
    license_plate: vals.license_plate,
    model: vals.model || undefined,
    vin: vals.vin || undefined,
    year: vals.year !== "" ? (vals.year as number) : undefined,
    capacity_kg: vals.capacity_kg !== "" ? (vals.capacity_kg as number) : undefined,
    volume_m3: vals.volume_m3 !== "" ? (vals.volume_m3 as number) : undefined,
    body_type_id: vals.kind === "single" ? (vals.body_type_id || undefined) : undefined,
    trailer_brand: vals.kind === "autopoezd" ? (vals.trailer_brand || undefined) : undefined,
    trailer_license_plate: vals.kind === "autopoezd" ? (vals.trailer_license_plate || undefined) : undefined,
    trailer_body_type_id: vals.kind === "autopoezd" ? (vals.trailer_body_type_id || undefined) : undefined,
    trailer_year: vals.kind === "autopoezd" && vals.trailer_year !== ""
      ? (vals.trailer_year as number)
      : undefined,
    carrier_id: vals.carrier_id || undefined,
    notes: vals.notes || undefined,
  });

  const createMut = useMutation({
    mutationFn: async (body: VehicleCreateRequest) =>
      (await api.post<Vehicle>("/api/v1/vehicles", body)).data,
    onSuccess: () => {
      notifications.show({ message: "ТС создано", color: "green" });
      void qc.invalidateQueries({ queryKey: ["vehicles"] });
      setEditing(undefined);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: VehicleUpdateRequest }) =>
      (await api.patch<Vehicle>(`/api/v1/vehicles/${id}`, body)).data,
    onSuccess: () => {
      notifications.show({ message: "ТС обновлено", color: "green" });
      void qc.invalidateQueries({ queryKey: ["vehicles"] });
      setEditing(undefined);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/api/v1/vehicles/${id}`)).data,
    onSuccess: () => {
      notifications.show({ message: "ТС удалено", color: "green" });
      void qc.invalidateQueries({ queryKey: ["vehicles"] });
      setConfirmDelete(null);
    },
    onError: (e) =>
      notifications.show({ title: "Ошибка", message: formatAPIError(e), color: "red" }),
  });

  const submit = () => {
    const formApi = formApiRef.current;
    if (!formApi) return;
    const v = formApi.values;
    // Двойная защита от ухода тела с пустыми обязательными полями (бэк отдаёт
    // 400 «Brand/LicensePlate required», но пользователь без понятного toast
    // не понимает где он ошибся).
    const missing: string[] = [];
    if (!v.brand?.trim()) missing.push("Марка тягача");
    if (!v.license_plate?.trim()) missing.push("Госномер тягача");
    if (v.kind === "autopoezd") {
      if (!v.trailer_brand?.trim()) missing.push("Марка прицепа");
      if (!v.trailer_license_plate?.trim()) missing.push("Госномер прицепа");
      if (!v.trailer_body_type_id) missing.push("Тип кузова прицепа");
    }
    if (missing.length > 0) {
      formApi.setErrors({
        brand: !v.brand?.trim() ? "Обязательно" : undefined,
        license_plate: !v.license_plate?.trim() ? "Обязательно" : undefined,
        trailer_brand: v.kind === "autopoezd" && !v.trailer_brand?.trim() ? "Обязательно" : undefined,
        trailer_license_plate: v.kind === "autopoezd" && !v.trailer_license_plate?.trim() ? "Обязательно" : undefined,
        trailer_body_type_id: v.kind === "autopoezd" && !v.trailer_body_type_id ? "Обязательно" : undefined,
      });
      notifications.show({
        title: "Заполните обязательные поля",
        message: missing.join(", "),
        color: "red",
      });
      return;
    }
    if (formApi.validate().hasErrors) return;
    const body = buildBody(formApi.values);
    // ВАЖНО: editing бывает трёх видов — undefined (закрыто), null («Создать»),
    // и объект. Объект может быть как существующим ТС (есть id → update), так и
    // «создание с предзаполненным перевозчиком» из карточки перевозчика
    // (?new=1&carrier_id=X → объект БЕЗ id). Раньше тут было `if (editing)`, из-за
    // чего такое создание уходило в updateMut с id=undefined → PATCH
    // /vehicles/undefined → бэк отвечал «invalid id». Решаем по наличию id.
    if (editing && editing.id) {
      const prev = editing;
      const updateBody: VehicleUpdateRequest = {
        ...body,
        clear_body_type: !body.body_type_id && !!prev.body_type_id,
        clear_carrier_id: !body.carrier_id && !!prev.carrier_id,
        clear_trailer_brand: body.kind === "single" && !!prev.trailer_brand,
        clear_trailer_plate: body.kind === "single" && !!prev.trailer_license_plate,
        clear_trailer_body_type: body.kind === "single" && !!prev.trailer_body_type_id,
      };
      updateMut.mutate({ id: prev.id, body: updateBody });
    } else {
      createMut.mutate(body);
    }
  };

  const columns: DataTableColumn<Vehicle>[] = [
    {
      key: "kind",
      header: "Тип",
      render: (v) => (
        <Badge
          color={v.kind === "autopoezd" ? "blue" : "gray"}
          variant="light"
          size="sm"
        >
          {v.kind === "autopoezd" ? "Автопоезд" : "Одиночная"}
        </Badge>
      ),
      width: 110,
    },
    {
      key: "transport",
      header: "Транспорт",
      render: (v) => {
        if (v.kind === "autopoezd") {
          return (
            <Text size="sm">
              <Text span fw={500}>{v.brand}</Text>
              {" "}
              <Text span c="dimmed" size="xs">{v.license_plate}</Text>
              {" + "}
              <Text span fw={500}>{v.trailer_brand ?? "?"}</Text>
              {" "}
              <Text span c="dimmed" size="xs">{v.trailer_license_plate ?? ""}</Text>
              {v.trailer_body_type_name && (
                <Badge ml={6} size="xs" variant="outline" color="teal">
                  {v.trailer_body_type_name}
                </Badge>
              )}
            </Text>
          );
        }
        return (
          <Text size="sm">
            <Text span fw={500}>{v.brand}</Text>
            {v.model && <Text span c="dimmed"> {v.model}</Text>}
            {" · "}
            <Text span c="dimmed" size="xs">{v.license_plate}</Text>
            {v.body_type_name && (
              <Badge ml={6} size="xs" variant="outline" color="gray">
                {v.body_type_name}
              </Badge>
            )}
          </Text>
        );
      },
      filter: {
        type: "text",
        getValue: (v) => `${v.brand ?? ""} ${v.model ?? ""} ${v.license_plate ?? ""}`.trim(),
        placeholder: "Поиск по марке / госномеру",
      },
    },
    {
      key: "carrier",
      header: "Перевозчик",
      render: (v) =>
        v.carrier_name ? (
          <Text size="sm">{v.carrier_name}</Text>
        ) : (
          <Text size="sm" c="dimmed">—</Text>
        ),
      filter: {
        type: "text",
        getValue: (v) => v.carrier_name ?? "",
        placeholder: "Поиск по перевозчику",
      },
    },
    {
      key: "actions",
      header: "",
      width: 60,
      render: (v) => (
        <Menu shadow="md" position="bottom-end">
          <Menu.Target>
            <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
              <IconDots size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconUsersGroup size={14} />}
              onClick={() => setDriversVehicle(v)}
            >
              Водители
            </Menu.Item>
            <Can perm="vehicles:write">
              <Menu.Item
                leftSection={<IconPencil size={14} />}
                onClick={() => setEditing(v)}
              >
                Редактировать
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={() => setConfirmDelete(v)}
              >
                Удалить
              </Menu.Item>
            </Can>
          </Menu.Dropdown>
        </Menu>
      ),
    },
  ];

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Транспорт</Title>
        <Can perm="vehicles:write">
          <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing(null)}>
            Создать
          </Button>
        </Can>
      </Group>

      <Group>
        <TextInput
          placeholder="Поиск по номеру, марке, VIN..."
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
            setPage(1);
          }}
          style={{ maxWidth: 320 }}
          leftSection={<IconCar size={16} />}
        />
        <Select
          placeholder="Все"
          data={KIND_OPTIONS}
          value={kindFilter}
          onChange={(v) => {
            setKindFilter(v || null);
            setPage(1);
          }}
          clearable
          style={{ minWidth: 160 }}
        />
        <Select
          placeholder="Все перевозчики"
          data={carrierOptions}
          value={carrierFilter}
          onChange={(v) => {
            setCarrierFilter(v || null);
            setPage(1);
          }}
          clearable
          style={{ minWidth: 220 }}
        />
      </Group>

      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        total={data?.total ?? 0}
        page={page}
        pageSize={50}
        onPageChange={setPage}
        rowKey={(v) => v.id}
        onRowClick={(v) => setEditing(v)}
      />

      <FormModal
        opened={editing !== undefined}
        onClose={() => setEditing(undefined)}
        title={editing?.id ? "Редактировать ТС" : "Новое транспортное средство"}
        onSubmit={submit}
        loading={createMut.isPending || updateMut.isPending}
        size="xl"
      >
        <VehicleForm initial={editing ?? undefined} onChange={setFormApi} />
      </FormModal>

      <ConfirmDialog
        opened={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && deleteMut.mutate(confirmDelete.id)}
        title="Удалить ТС?"
        message={`ТС "${confirmDelete?.brand} (${confirmDelete?.license_plate})" будет удалено. Действие необратимо.`}
        loading={deleteMut.isPending}
      />

      {driversVehicle && (
        <VehicleDrivers
          vehicle={driversVehicle}
          opened={!!driversVehicle}
          onClose={() => setDriversVehicle(null)}
        />
      )}
    </Stack>
  );
}
